'use client';

import {useEffect, useMemo, useState} from 'react';
import Link from 'next/link';
import styles from './Articles.module.css';
import {Article, formatArticleData, getArticleImagePath,} from '@/shared/pages/articlesShared';
import {consts} from "@/shared/commonShared";
import ExportedImage from "next-image-export-optimizer";

type Props = {
	initialArticles: Article[];
};

export default function Articles({ initialArticles }: Props) {
	const [page, setPage] = useState(1);
	const PAGE_SIZE = 10;

	const [selectedTags, setSelectedTags] = useState<string[]>([]);

	// build all tags once from initial data
	const allTags = useMemo(() => {
		const tagsSet = new Set<string>();
		initialArticles.forEach((item) => {
			(item.tags || []).forEach((t) => tagsSet.add(t));
		});
		return Array.from(tagsSet).sort();
	}, [initialArticles]);

	// filter by tags and published
	const filteredNews = useMemo(() => {
		if (!selectedTags.length) return initialArticles;
		return initialArticles.filter((item) => {
			const itemTags = item.tags || [];
			return selectedTags.every((t) => itemTags.includes(t));
		});
	}, [initialArticles, selectedTags]);

	const totalPages = Math.max(1, Math.ceil(filteredNews.length / PAGE_SIZE));
	const pageItems = useMemo(() => {
		const start = (page - 1) * PAGE_SIZE;
		return filteredNews.slice(start, start + PAGE_SIZE);
	}, [filteredNews, page, PAGE_SIZE]);

	useEffect(() => {
		if (page > totalPages) setPage(totalPages);
	}, [totalPages, page]);

	const go = (p: number) => setPage(Math.min(Math.max(1, p), totalPages));

	return (
		<section className={"section-blog-7 border-bottom py-"+consts.PADDING}>
			<div className="container">
				<div className="row">
					<div className="col text-center">
						<h3 className="ds-3">Recent Posts</h3>
						<span className="fs-5 fw-medium">
              Explore the insights and trends shaping our industry
            </span>
					</div>
				</div>

				<div className="row">
					<div className={"col-lg-10 mx-lg-auto mt-"+consts.PADDING}>
						{/* Tag filters */}
						{initialArticles.length > 0 && (
							<div className="text-center">
								<div className="button-group filter-button-group filter-menu-active">
									<button
										className={
											selectedTags.length === 0
												? "active btn btn-md btn-filter me-"+consts.PADDING_SMALL+" mb-"+consts.PADDING_SMALL
												: "btn btn-md btn-filter me-"+consts.PADDING_SMALL+" mb-"+consts.PADDING_SMALL
										}
										onClick={() => setSelectedTags([])}
									>
										All
									</button>

									{allTags.map((tag) => (
										<button
											key={tag}
											className={
												selectedTags.includes(tag)
													? "active btn btn-md btn-filter me-"+consts.PADDING_SMALL+" mb-"+consts.PADDING_SMALL
													: "btn btn-md btn-filter me-"+consts.PADDING_SMALL+" mb-"+consts.PADDING_SMALL
											}
											onClick={() => setSelectedTags([tag])}
										>
											{tag}
										</button>
									))}
								</div>
							</div>
						)}

						{/* Posts */}
						{pageItems.map((item) => (
							<Link
								href={`/blog/${item.urlSlug}`}
								key={item.id}
								className={styles.blogCard+" border-top position-relative d-flex flex-column flex-md-row align-items-center m-0 mt-2 py-"+consts.PADDING_SMALL}
							>
								<div className={styles.blogThumb}>
									<ExportedImage
										src={getArticleImagePath(item.thumbnailUrl)}
										alt={item.title}
										width={400}
										height={225}              // 16:9-ish
										sizes="(max-width: 768px) 100vw, 400px"
										className={styles.blogThumbImg} // if you want extra styling
										loading="lazy"
									/>
								</div>
								<div className={"me-auto mt-md-0 ms-md-"+consts.PADDING}>
									<div className="tags d-flex gap-2">
									{item.tags && item.tags.map((tag, index) => (
										<span key={index} className="tag-spacing fs-8 fs-lg-7 fw-bold text-linear-2 text-uppercase">
										  {tag}
										</span>
									))}
									</div>
									<h6 className={"my-"+consts.PADDING_SMALL}>
											{item.title}
									</h6>
									<p className={"mb-"+consts.PADDING_SMALL} dangerouslySetInnerHTML={{__html: item.shortContent}} />
									<div className="text-neutral-500 fs-8">
										{formatArticleData(item.createdAt)}
									</div>
								</div>
							</Link>
						))}

						<div className="border-top" />

						{totalPages > 1 && (
							<div className="container">
								<div className={"row pt-5 text-start pt-"+consts.PADDING}>
									<div className="d-flex justify-content-center align-items-center">
										<nav aria-label="Page navigation">
											<ul className="pagination gap-2">
												{/* prev */}
												<li className="page-item">
													<button
														className="icon-xl fs-5 page-link pagination_item border-0 rounded-circle icon-shape fw-bold bg-neutral-100 text-900"
														onClick={() => go(page - 1)}
														aria-label="Previous"
														disabled={page === 1}
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															width={22}
															height={22}
															viewBox="0 0 22 22"
															fill="none"
														>
															<path
																className="stroke-dark"
																d="M9.49554 6.5L4.78125 11L9.49554 15.5"
																stroke="#111827"
																strokeWidth="1.28571"
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
															<path
																className="stroke-dark"
																d="M17.2143 11H5"
																stroke="#111827"
																strokeWidth="1.28571"
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
														</svg>
													</button>
												</li>

												{/* numbers */}
												{Array.from({ length: totalPages }, (_, i) => i + 1).map(
													(n) => (
														<li className="page-item" key={n}>
															<button
																className={`icon-xl fs-5 page-link pagination_item border-0 rounded-circle icon-shape fw-bold ${
																	n === page
																		? 'bg-primary text-white'
																		: 'bg-neutral-100 text-900'
																}`}
																onClick={() => go(n)}
															>
																{n}
															</button>
														</li>
													)
												)}

												{/* next */}
												<li className="page-item">
													<button
														className="icon-xl fs-5 page-link pagination_item border-0 rounded-circle icon-shape fw-bold bg-neutral-100 text-900"
														onClick={() => go(page + 1)}
														aria-label="Next"
														disabled={page === totalPages}
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															width={22}
															height={22}
															viewBox="0 0 22 22"
															fill="none"
														>
															<path
																className="stroke-dark"
																d="M12.5 6.5L17.2143 11L12.5 15.5"
																stroke="#111827"
																strokeWidth="1.28571"
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
															<path
																className="stroke-dark"
																d="M16.9955 11H4.78125"
																stroke="#111827"
																strokeWidth="1.28571"
																strokeLinecap="round"
																strokeLinejoin="round"
															/>
														</svg>
													</button>
												</li>
											</ul>
										</nav>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}
